import { HttpClient, HttpEvent, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { InvestmentSecurityPurchaseFormView } from '../../models/debt-management/InvestmentSecurityPurchaseFormView';
import { UtilityService } from '../utility.service';
import { FormSearch } from 'app/models/dashboard/FormStatus';
import { BaseDashboard } from 'app/models/dashboard/BaseDashboard';

@Injectable({
	providedIn: 'root',
})
export class InvestmentSecurityPurchaseService {
	private readonly urlPath = `${environment.apiUrl}debt-management/clients/`;
	private readonly headers = new HttpHeaders({
		'Cache-Control': 'no-cache, no-store, must-revalidate, post-check=0, pre-check=0',
	});

	constructor(private httpClient: HttpClient, private utilityService: UtilityService) {}

	getFormsFor(clientId: string, userId: string, search: FormSearch): Observable<BaseDashboard<any>> {
		const url = `${
			this.urlPath
		}${clientId}/forms/investment-security-purchase-form/users/${userId}?${this.utilityService.objectToQueryString(
			search
		)}`;
		return this.httpClient.get<BaseDashboard<any>>(url).pipe();
	}

	createForm(
		clientId: string,
		request: InvestmentSecurityPurchaseFormView,
		files: File[] = []
	): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.post<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form`,
			this.buildFormData(request, files)
		);
	}

	updateForm(
		clientId: string,
		id: string,
		request: InvestmentSecurityPurchaseFormView,
		files: File[] = []
	): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.put<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}`,
			this.buildFormData(request, files)
		);
	}

	checkDuplicate(clientId: string, cusip: string, formId: string | null): Observable<boolean> {
		return this.httpClient.get<boolean>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/check-duplicate`,
			{
				params: formId ? { cusip, formId } : { cusip },
				headers: this.headers,
			}
		);
	}

	getForm(clientId: string, id: string): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.get<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}`,
			{ headers: this.headers }
		);
	}

	submitForApproval(clientId: string, id: string): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.post<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}/approval`,
			{}
		);
	}

	sendForReview(clientId: string, id: string): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.post<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}/review`,
			{}
		);
	}

	uploadFile(clientId: string, id: string, file: File): Observable<HttpEvent<unknown>> {
		const formData = new FormData();
		formData.append('files', file, file.name);
		return this.httpClient.post<unknown>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}/docs`,
			formData,
			{ reportProgress: true, observe: 'events' }
		);
	}

	rejectForm(
		clientId: string,
		id: string,
		rejection: { reason: string; workflowId: number }
	): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.post<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}/decline`,
			rejection
		);
	}

	approveForm(clientId: string, id: string, userId: string): Observable<InvestmentSecurityPurchaseFormView> {
		return this.httpClient.post<InvestmentSecurityPurchaseFormView>(
			`${this.urlPath}${clientId}/forms/investment-security-purchase-form/${id}/approve`,
			{ userId }
		);
	}

	private buildFormData(request: InvestmentSecurityPurchaseFormView, files: File[]): FormData {
		const formData = new FormData();
		formData.append('model', JSON.stringify(request));
		files.forEach((file) => formData.append('files', file, file.name));
		return formData;
	}
}
